import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowLeft, ArrowUpRight } from "lucide-react";
import { MaskLine } from "./Reveal";
import { scrollToId } from "../lib/format";

export default function Hero({ slides, settings }) {
  const [index, setIndex] = useState(0);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const overlayY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  const list = slides.length ? slides : [{ image: settings?.hero_image, title: "MO by Vir" }];

  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i + 1) % list.length), 5000);
    return () => clearInterval(t);
  }, [list.length]);

  const go = (dir) => setIndex((i) => (i + dir + list.length) % list.length);
  const current = list[index];

  return (
    <section
      id="inicio"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-cream"
    >
      {/* Background slider image with parallax */}
      <motion.div style={{ y }} className="absolute inset-0 z-0">
        <AnimatePresence mode="sync">
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 1.08 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0"
          >
            <img
              src={current.image}
              alt={current.title || "MO by Vir"}
              className="w-full h-[115%] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-cream/85 via-cream/40 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-cream/70 via-transparent to-cream/20" />
          </motion.div>
        </AnimatePresence>
      </motion.div>

      {/* Editorial content */}
      <motion.div
        style={{ y: overlayY }}
        className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 min-h-screen flex flex-col justify-center pt-40 md:pt-48"
      >
        <MaskLine delay={0.4}>
          <span className="text-sm md:text-lg uppercase tracking-[0.35em] text-rose font-bold">
            Tienda 100% virtual · @Mo_ByVir
          </span>
        </MaskLine>

        <h1 className="mt-6 font-serif font-light text-espresso leading-[0.9] text-6xl sm:text-7xl md:text-8xl lg:text-[8.5rem]">
          <MaskLine delay={0.55}>Perfumería</MaskLine>
          <MaskLine delay={0.7} className="italic text-rose pl-8 md:pl-24">
            en miniatura
          </MaskLine>
        </h1>

        <MaskLine delay={0.95}>
          <p className="mt-8 max-w-md text-taupe text-base md:text-lg font-light leading-relaxed">
            {settings?.brand_tagline ||
              "Mini perfumes, carteras y accesorios seleccionados con obsesión por el detalle."}
          </p>
        </MaskLine>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-shop-btn"
            onClick={() => scrollToId("catalogo")}
            className="group inline-flex items-center gap-3 bg-espresso text-cream rounded-full pl-7 pr-5 py-4 text-xs uppercase tracking-[0.2em] hover:bg-rose transition-colors duration-500"
          >
            Explorar catálogo
            <span className="w-8 h-8 rounded-full bg-cream/15 flex items-center justify-center group-hover:translate-x-1 transition-transform">
              <ArrowRight size={15} />
            </span>
          </button>
          <button
            data-testid="hero-offers-btn"
            onClick={() => scrollToId("ofertas")}
            className="inline-flex items-center gap-2 text-espresso text-xs uppercase tracking-[0.2em] border-b border-espresso pb-1 hover:text-rose hover:border-rose transition-colors"
          >
            Ver ofertas <ArrowUpRight size={15} />
          </button>
        </motion.div>

        {/* Slider caption + controls */}
        <div className="mt-auto mb-10 flex items-end justify-between">
          <AnimatePresence mode="wait">
            <motion.div
              key={`cap-${index}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.5 }}
              className="max-w-xs"
            >
              <span className="text-[10px] uppercase tracking-[0.25em] text-taupe">
                {String(index + 1).padStart(2, "0")} / {String(list.length).padStart(2, "0")}
              </span>
              <p className="font-serif text-xl text-espresso mt-1">{current.title}</p>
              {current.subtitle && (
                <p className="text-xs text-taupe">{current.subtitle}</p>
              )}
            </motion.div>
          </AnimatePresence>

          <div className="flex items-center gap-3">
            <button
              data-testid="hero-prev"
              onClick={() => go(-1)}
              className="w-11 h-11 rounded-full border border-espresso/30 flex items-center justify-center hover:bg-espresso hover:text-cream transition-colors"
              aria-label="Anterior"
            >
              <ArrowLeft size={16} />
            </button>
            <button
              data-testid="hero-next"
              onClick={() => go(1)}
              className="w-11 h-11 rounded-full border border-espresso/30 flex items-center justify-center hover:bg-espresso hover:text-cream transition-colors"
              aria-label="Siguiente"
            >
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
